import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import ShipmentDetailsModal from './ShipmentDetailsModal';
import RateConfirmationModal from './RateConfirmationModal';
import CreateShipmentModal from './CreateShipmentModal';
import BulkUploadShipments from './BulkUploadShipments';
import { Plus, Upload, Download } from 'lucide-react';
import API from '../api/api';

// ─── Stop type codes (mirrors PICKUP_STOP_TYPES / DELIVERY_STOP_TYPES in core/models.py) ──
const PICKUP_STOP_TYPES   = ['CL', 'PU', 'LD', 'PL'];
const DELIVERY_STOP_TYPES = ['CU', 'DL', 'UL'];

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

const STATUS_OPTIONS = ['pending', 'in_transit', 'delivered', 'cancelled'];

// ─── Cell value helpers ───────────────────────────────────────────────────────
const parseDate = (d) => (d ? new Date(`${d}T00:00:00`) : null);

// Excel "Load Detail" renders dates as mm-dd-yy
const fmtDate = (d) => {
  const dt = parseDate(d);
  if (!dt || isNaN(dt)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getMonth() + 1)}-${p(dt.getDate())}-${String(dt.getFullYear()).slice(-2)}`;
};

// Excel renders times as h:mm on a 24h clock; API sends "HH:MM:SS"
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':');
  if (h === undefined || m === undefined) return '';
  return `${parseInt(h, 10)}:${m}`;
};

const fmtMonth = (d) => {
  const dt = parseDate(d);
  return dt && !isNaN(dt) ? MONTHS[dt.getMonth()] : '';
};

const fmtMoney = (v, digits = 2) => {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || isNaN(n)) return '';
  return n.toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  });
};

const fmtMiles = (v) => {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || isNaN(n)) return '';
  return Math.round(n).toLocaleString('en-US');
};

// Origin = first pickup stop, destination = last delivery stop. Falls back to
// parsing the serializer's "…, City, ST ZIP" address string when stops are absent.
const originStop = (s) => (s.stops || []).find((st) => PICKUP_STOP_TYPES.includes(st.stop_type));
const destStop   = (s) => [...(s.stops || [])].reverse().find((st) => DELIVERY_STOP_TYPES.includes(st.stop_type));

const cityStateFromAddress = (addr) => {
  if (!addr) return { city: '', state: '' };
  const parts = addr.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return { city: '', state: '' };
  const tail = parts[parts.length - 1];              // "MO 63101" or "MO"
  const state = (tail.split(/\s+/)[0] || '').slice(0, 2).toUpperCase();
  return { city: parts[parts.length - 2] || '', state };
};

const originCityState = (s) => {
  const st = originStop(s);
  if (st && (st.city || st.state)) return { city: st.city || '', state: st.state || '' };
  return cityStateFromAddress(s.origin_address);
};

const destCityState = (s) => {
  const st = destStop(s);
  if (st && (st.city || st.state)) return { city: st.city || '', state: st.state || '' };
  return cityStateFromAddress(s.destination_address);
};

// Excel column "D": 1 = company driver, 999 = outside carrier
const driverCode = (s) => (s.driver ? '1' : '999');

// ─── Rate confirmation cell ───────────────────────────────────────────────────
const rateConLabel = (s) => {
  const rc = s.rate_confirmation_summary;
  if (!rc) return 'Send';
  if (rc.status === 'signed') return 'Signed';
  if (rc.status === 'sent') return 'Sent';
  return 'Draft';
};

const rateConClass = (s) => {
  const rc = s.rate_confirmation_summary;
  if (!rc) return 'border-primary-300 text-primary-700 bg-white hover:bg-primary-50';
  if (rc.status === 'signed') return 'border-green-300 text-green-800 bg-green-50 hover:bg-green-100';
  if (rc.status === 'sent') return 'border-blue-300 text-blue-800 bg-blue-50 hover:bg-blue-100';
  return 'border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100';
};

const rateConTitle = (s) => {
  const rc = s.rate_confirmation_summary;
  if (!rc) return 'Send a rate confirmation to a carrier';
  const when = rc.sent_at ? ` on ${new Date(rc.sent_at).toLocaleDateString()}` : '';
  return `${rc.confirmation_number} — ${rc.carrier_name}${when}`;
};

// Excel columns B/C/D show the carrier name for loads run by an outside carrier
const truckLabel   = (s) => s.truck_number   || s.carrier_name || '';
const trailerLabel = (s) => s.trailer_number || s.carrier_name || '';
const driverLabel  = (s) => s.driver_detail?.full_name || s.carrier_name || '';

// ─── Column definitions (mirrors the Load Detail tab, A→X) ────────────────────
// `sort` keys are limited to what ShipmentViewSet.ordering_fields exposes.
// `entry` says how the blank row at the top of the grid types this column in:
// `draft` is the key it writes in the draft, and the payload is assembled from
// those keys in buildCreatePayload. A column with no `entry` is derived or
// assigned by the server, so its entry cell shows a hint instead of an input.
const COLUMNS = [
  { key: 'pro',      label: 'Pro#',         w: 'w-20',  sort: 'pro_number',    sticky: true,
    render: (s) => s.pro_number, entryHint: 'auto' },
  { key: 'truck',    label: 'Truck#',       w: 'w-24',  render: truckLabel,
    entry: { draft: 'truck_number', type: 'text' } },
  { key: 'trailer',  label: 'Trailer#',     w: 'w-24',  render: trailerLabel,
    entry: { draft: 'trailer_number', type: 'text' } },
  { key: 'drvr',     label: 'DRVR',         w: 'w-28',  render: driverLabel },
  { key: 'd',        label: 'D',            w: 'w-14',  title: '1 = company driver, 999 = outside carrier',
    render: driverCode },
  { key: 'customer', label: 'Customer',     w: 'w-28',  render: (s) => s.shipper_name || '',
    entry: { draft: 'shipper_name', type: 'partner' } },
  { key: 'custld',   label: 'Customer LD#', w: 'w-64',  align: 'left', clamp: 'max-w-[16rem]',
    render: (s) => s.cust_po_number || s.po_number || '',
    entry: { draft: 'cust_po_number', type: 'text' } },
  { key: 'pudate',   label: 'PU Date',      w: 'w-24',  sort: 'pickup_date',
    render: (s) => fmtDate(s.actual_pickup_date || s.pickup_date),
    entry: { draft: 'pickup_date', type: 'date', required: true } },
  { key: 'putime',   label: 'PU Tme',       w: 'w-20',  render: (s) => fmtTime(s.pickup_time),
    entry: { draft: 'pickup_time', type: 'time' } },
  { key: 'month',    label: 'Month',        w: 'w-24',
    render: (s) => fmtMonth(s.actual_pickup_date || s.pickup_date),
    entryFrom: (draft) => fmtMonth(draft.pickup_date) },
  { key: 'orgcty',   label: 'Org CTY',      w: 'w-32',  render: (s) => originCityState(s).city,
    entry: { draft: 'origin_city', type: 'text' } },
  { key: 'orgst',    label: 'Org ST',       w: 'w-16',  render: (s) => originCityState(s).state,
    entry: { draft: 'origin_state', type: 'text', maxLength: 2, uppercase: true } },
  { key: 'deldate',  label: 'DEL Date',     w: 'w-24',  sort: 'delivery_date',
    render: (s) => fmtDate(s.actual_delivery_date || s.delivery_date),
    entry: { draft: 'delivery_date', type: 'date', required: true } },
  { key: 'deltime',  label: 'DEL Time',     w: 'w-20',  render: (s) => fmtTime(s.delivery_time),
    entry: { draft: 'delivery_time', type: 'time' } },
  { key: 'destcty',  label: 'Dest CTY',     w: 'w-32',  render: (s) => destCityState(s).city,
    entry: { draft: 'destination_city', type: 'text' } },
  { key: 'destst',   label: 'Dest ST',      w: 'w-16',  render: (s) => destCityState(s).state,
    entry: { draft: 'destination_state', type: 'text', maxLength: 2, uppercase: true } },
  // The grid shows total_miles, which the server derives; the cell to type into
  // is last_mile, the same column the bulk upload's "L Miles" feeds.
  { key: 'lmiles',   label: 'L Miles',      w: 'w-20',  num: true, render: (s) => fmtMiles(s.total_miles),
    entry: { draft: 'last_mile', type: 'number' } },
  { key: 'rate',     label: 'Gross Rate',   w: 'w-28',  num: true, sort: 'rate',
    render: (s) => fmtMoney(s.rate),
    entry: { draft: 'rate', type: 'number' } },
  { key: 'labor',    label: 'Labor',        w: 'w-28',  num: true, render: (s) => fmtMoney(s.cost),
    entry: { draft: 'cost', type: 'number' } },
  { key: 'rpm',      label: 'RPM',          w: 'w-20',  num: true,
    render: (s) => fmtMoney(s.revenue_per_mile, 3),
    entryFrom: (draft) => {
      const miles = Number(draft.last_mile);
      const rate = Number(draft.rate);
      if (!miles || isNaN(miles) || isNaN(rate)) return '';
      return fmtMoney(rate / miles, 3);
    } },
  { key: 'comments', label: 'Comments',     w: 'w-56',  align: 'left', clamp: 'max-w-[14rem]',
    render: (s) => s.special_instructions || '',
    entry: { draft: 'special_instructions', type: 'text' } },
];

const ENTRY_COLUMNS = COLUMNS.filter((c) => c.entry);

const EMPTY_DRAFT = Object.fromEntries(ENTRY_COLUMNS.map((c) => [c.entry.draft, '']));

const isDraftDirty = (draft) => Object.values(draft).some((v) => String(v || '').trim() !== '');

// ─── New-shipment row ─────────────────────────────────────────────────────────
// A blank row pinned under the header, typed into like the next line of the
// spreadsheet. Enter commits it, Escape clears it, Tab walks the cells.

// Matches a typed Customer against the trading partners we already have. Exact
// name or EDI id first, then a unique case-insensitive prefix, so "aldi" lands on
// "ALDI" but an ambiguous stub does not silently pick one.
const resolvePartner = (typed, partners) => {
  const wanted = String(typed || '').trim().toLowerCase();
  if (!wanted) return null;
  const exact = partners.find(
    (p) => (p.name || '').toLowerCase() === wanted || (p.edi_id || '').toLowerCase() === wanted
  );
  if (exact) return exact;
  const prefixed = partners.filter((p) => (p.name || '').toLowerCase().startsWith(wanted));
  return prefixed.length === 1 ? prefixed[0] : null;
};

// Only writable API fields, and only ones the user actually filled in. Shipment.rate
// and friends are non-null decimals, so a blank cell is omitted rather than sent as null.
const buildCreatePayload = (draft, { carrierId, partner }) => {
  const text = (key) => String(draft[key] || '').trim();
  const payload = {
    carrier: carrierId,
    status: 'pending',
    pickup_date: draft.pickup_date,
    delivery_date: draft.delivery_date,
  };

  if (partner) payload.shipper = partner.id;
  if (draft.pickup_time) payload.pickup_time = draft.pickup_time;
  if (draft.delivery_time) payload.delivery_time = draft.delivery_time;

  for (const key of ['cust_po_number', 'truck_number', 'trailer_number', 'special_instructions']) {
    if (text(key)) payload[key] = text(key);
  }

  for (const key of ['rate', 'cost', 'last_mile']) {
    const n = Number(text(key));
    if (text(key) !== '' && !isNaN(n)) payload[key] = String(n);
  }

  // Origin and destination become Stop rows on the shipment. City and state go
  // over as their own fields so the grid reads them back as the columns they are,
  // rather than re-parsing a joined address string.
  for (const [cell, field] of [
    ['origin_city', 'create_pickup_city'],
    ['origin_state', 'create_pickup_state'],
    ['destination_city', 'create_delivery_city'],
    ['destination_state', 'create_delivery_state'],
  ]) {
    if (text(cell)) payload[field] = text(cell);
  }

  return payload;
};

const formatCreateError = (data) => {
  if (!data) return 'Could not add the load';
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.error === 'string') return data.error;
  if (typeof data === 'object') {
    const parts = Object.entries(data).map(([field, messages]) => {
      const text = Array.isArray(messages) ? messages.join(', ') : String(messages);
      return `${field}: ${text}`;
    });
    if (parts.length) return parts.join('; ');
  }
  return 'Could not add the load';
};

function NewShipmentRow({
  draft, setDraft, onSubmit, onClear, saving, partners, cellBase, top, firstInputRef,
}) {
  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClear();
    }
  };

  const dirty = isDraftDirty(draft);
  const inputBase =
    'w-full bg-transparent text-[11px] px-0.5 py-0 border-0 outline-none ' +
    'focus:bg-white focus:ring-1 focus:ring-primary-400 rounded-sm disabled:opacity-50';

  const renderInput = (col, index) => {
    const spec = col.entry;
    const value = draft[spec.draft] ?? '';
    const common = {
      value,
      disabled: saving,
      onKeyDown,
      ref: index === 0 ? firstInputRef : undefined,
      className: `${inputBase} ${col.num ? 'text-right' : col.align === 'left' ? 'text-left' : 'text-center'}`,
      'aria-label': col.label,
    };

    if (spec.type === 'partner') {
      const unresolved = String(value).trim() && !resolvePartner(value, partners);
      return (
        <>
          <input
            {...common}
            list="dashboard-trading-partners"
            placeholder="Customer"
            title={unresolved ? 'No customer by that name — pick one from the list' : undefined}
            className={`${common.className} ${unresolved ? 'ring-1 ring-red-400 rounded-sm' : ''}`}
            onChange={(e) => setField(spec.draft, e.target.value)}
          />
          <datalist id="dashboard-trading-partners">
            {partners.map((p) => <option key={p.id} value={p.name} />)}
          </datalist>
        </>
      );
    }

    return (
      <input
        {...common}
        type={spec.type === 'number' ? 'number' : spec.type === 'date' ? 'date' : spec.type === 'time' ? 'time' : 'text'}
        step={spec.type === 'number' ? 'any' : undefined}
        maxLength={spec.maxLength}
        required={spec.required}
        placeholder={spec.required ? 'required' : undefined}
        onChange={(e) => {
          const next = spec.uppercase ? e.target.value.toUpperCase() : e.target.value;
          setField(spec.draft, next);
        }}
      />
    );
  };

  // The whole row rides under the header rather than scrolling away with the
  // data, so it stays reachable however far down the list you are.
  const pinned = 'sticky bg-primary-50 z-20';
  let entryIndex = -1;

  return (
    <tr className="bg-primary-50">
      {COLUMNS.map((col) => {
        if (col.entry) entryIndex += 1;
        const align = col.align === 'left' ? 'text-left' : col.num ? 'text-right' : 'text-center';
        return (
          <td
            key={col.key}
            style={{ top }}
            className={`${cellBase} ${col.w} ${align} ${pinned} ${col.sticky ? 'left-0 z-30' : ''}`}
          >
            {col.entry ? renderInput(col, entryIndex)
              : col.sticky ? (
                <span className="font-medium text-primary-700" title="Assigned when the load is saved">
                  + {col.entryHint}
                </span>
              ) : (
                <span className="text-gray-400">{col.entryFrom ? col.entryFrom(draft) : ''}</span>
              )}
          </td>
        );
      })}

      {/* Status is always 'pending' on a brand new load — it is the board's first column of work */}
      <td style={{ top }} className={`${cellBase} w-28 text-center text-gray-400 ${pinned}`}>
        pending
      </td>

      <td style={{ top }} className={`${cellBase} w-24 text-center ${pinned}`}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onSubmit}
            disabled={saving || !dirty}
            title="Add this load (Enter)"
            className="flex-1 text-[11px] rounded px-1 py-0.5 text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
          {dirty && !saving && (
            <button
              type="button"
              onClick={onClear}
              title="Clear the row (Esc)"
              className="text-[11px] px-1 py-0.5 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main Dashboard — Load Detail grid ────────────────────────────────────────
// The grid scrolls rather than paginating: the server still hands out pages, but
// each one is appended as the bottom of the list comes into view, so the sheet
// reads as one continuous list the way the spreadsheet it mirrors does.
const PAGE_SIZE = 100;

function Dashboard() {
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  const [dateRange, setDateRange] = useState(30);   // days; 0 = all time
  const [shipmentsCount, setShipmentsCount] = useState(0);

  // Search is only applied on submit so typing doesn't refetch on every keystroke
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  // Excel sheet is sorted oldest-first by pickup date
  const [ordering, setOrdering] = useState('pickup_date');

  const [detailsShipment, setDetailsShipment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Rate confirmations open straight off a row — covering a load is its own job,
  // separate from editing the load's details.
  const [rateConShipment, setRateConShipment] = useState(null);
  const [rateConOpen, setRateConOpen] = useState(false);

  // Toolbar actions that used to live on the Shipments page
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exporting, setExporting] = useState(null);   // 'csv' | 'xlsx' while downloading
  const [actionError, setActionError] = useState(null);

  // Tracks which shipment rows have an in-flight status PATCH
  const [updatingStatus, setUpdatingStatus] = useState(new Set());

  // The blank row at the top of the grid
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState(null);
  const [partners, setPartners] = useState([]);
  const [carrierId, setCarrierId] = useState(null);
  const firstInputRef = useRef(null);

  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const headerRowRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Guards the appending fetch: a stale page must not land in a list that has
  // since been refiltered, and the observer can fire again before one returns.
  const nextPageRef = useRef(1);
  const inFlightRef = useRef(false);
  const requestRef = useRef(0);

  const fetchPage = useCallback(async (page, { append }) => {
    const token = append ? requestRef.current : (requestRef.current += 1);
    if (append) setLoadingMore(true); else setShipmentsLoading(true);
    inFlightRef.current = true;
    try {
      const params = new URLSearchParams({ page, page_size: PAGE_SIZE, ordering });
      if (dateRange) params.set('days', dateRange);
      if (appliedSearch) params.set('search', appliedSearch);
      const { data } = await API.get(`shipments/?${params}`);

      if (token !== requestRef.current) return;   // filters changed under us

      // Support both paginated ({results, count, next}) and plain array responses
      const rows = Array.isArray(data) ? data : (data.results || []);
      setShipments((prev) => (append ? [...prev, ...rows] : rows));
      setShipmentsCount(Array.isArray(data) ? rows.length : (data.count ?? rows.length));
      setHasMore(Array.isArray(data) ? false : Boolean(data.next));
      nextPageRef.current = page + 1;
      setError(null);
    } catch (err) {
      console.error('Error fetching shipments:', err);
      if (token === requestRef.current) {
        setError('Failed to load shipments. Please try again later.');
        setHasMore(false);
      }
    } finally {
      inFlightRef.current = false;
      if (append) setLoadingMore(false); else setShipmentsLoading(false);
    }
  }, [dateRange, ordering, appliedSearch]);

  // Any change to the filters starts the list over from page one
  useEffect(() => {
    nextPageRef.current = 1;
    setShipments([]);
    setHasMore(false);
    fetchPage(1, { append: false });
  }, [fetchPage]);

  const reload = useCallback(() => {
    nextPageRef.current = 1;
    fetchPage(1, { append: false });
  }, [fetchPage]);

  // Append the next page when the bottom of the list comes into view. An
  // observer rather than a scroll handler, so a first page too short to scroll
  // still pulls the next one in.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !hasMore) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || inFlightRef.current) return;
      fetchPage(nextPageRef.current, { append: true });
    }, { root, rootMargin: '400px' });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, fetchPage, shipments.length]);

  // The new-shipment row pins itself directly under the header, whose height
  // depends on the rendered font, so measure it rather than guessing.
  useLayoutEffect(() => {
    const row = headerRowRef.current;
    if (!row) return undefined;
    const measure = () => setHeaderHeight(row.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [shipmentsLoading, error]);

  // Who the new load belongs to, and the customers it can be booked for
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [user, partnerList] = await Promise.all([
          API.get('auth/user/'),
          API.get('trading-partners/?page_size=200'),
        ]);
        if (cancelled) return;
        setCarrierId(user.data.organization ?? null);
        const rows = partnerList.data.results || partnerList.data;
        setPartners(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error('Error loading entry-row reference data:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setAppliedSearch('');
  };

  // Exports what the grid is showing: same date window and search.
  const handleExport = async (format) => {
    setExporting(format);
    setActionError(null);
    try {
      const response = await API.post(
        'export-shipments/',
        { format, search: appliedSearch || undefined, days: dateRange || undefined },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.href = url;
      link.setAttribute('download', `shipments_${timestamp}.${format === 'xlsx' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting shipments:', err);
      setActionError('Failed to export shipments.');
    } finally {
      setExporting(null);
    }
  };

  const handleSort = (field) => {
    setOrdering((prev) => (prev === field ? `-${field}` : field));
  };

  const clearDraft = () => {
    setDraft(EMPTY_DRAFT);
    setDraftError(null);
  };

  const submitDraft = async () => {
    if (savingDraft) return;
    setDraftError(null);

    if (!carrierId) {
      setDraftError('Your account has no organization, so a load cannot be assigned a carrier.');
      return;
    }

    const missing = ENTRY_COLUMNS
      .filter((c) => c.entry.required && !String(draft[c.entry.draft] || '').trim())
      .map((c) => c.label);
    if (missing.length) {
      setDraftError(`Fill in ${missing.join(' and ')} before adding the load.`);
      return;
    }

    const typedPartner = String(draft.shipper_name || '').trim();
    const partner = resolvePartner(typedPartner, partners);
    if (typedPartner && !partner) {
      setDraftError(`No customer matches “${typedPartner}”. Pick one from the list.`);
      return;
    }

    setSavingDraft(true);
    try {
      const { data } = await API.post('shipments/', buildCreatePayload(draft, { carrierId, partner }));
      // Show it straight away rather than waiting on a refetch. If it falls
      // outside the current filter it drops out the next time the list reloads.
      setShipments((prev) => [data, ...prev]);
      setShipmentsCount((n) => n + 1);
      clearDraft();
      firstInputRef.current?.focus();
    } catch (err) {
      console.error('Error adding shipment:', err);
      setDraftError(formatCreateError(err.response?.data));
    } finally {
      setSavingDraft(false);
    }
  };

  const handleStatusChange = async (e, shipment) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    if (!newStatus || newStatus === shipment.status) return;
    setUpdatingStatus((prev) => new Set(prev).add(shipment.id));
    try {
      await API.patch(`shipments/${shipment.id}/`, { status: newStatus });
      setShipments((prev) =>
        prev.map((s) => (s.id === shipment.id ? { ...s, status: newStatus } : s))
      );
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingStatus((prev) => { const n = new Set(prev); n.delete(shipment.id); return n; });
    }
  };

  const sortIndicator = (field) => {
    if (ordering === field) return ' ▲';
    if (ordering === `-${field}`) return ' ▼';
    return '';
  };

  // Shared cell chrome — thin grid lines and centered text, like the spreadsheet
  const cellBase = 'border border-gray-300 px-2 py-1 whitespace-nowrap overflow-hidden text-ellipsis';

  return (
    <>
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Toolbar */}
          <div className="px-4 py-3 border-b border-gray-200 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Load Detail</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {shipmentsCount.toLocaleString()} load{shipmentsCount === 1 ? '' : 's'}{' '}
                {dateRange ? `in the last ${dateRange} days` : 'all time'}
                {appliedSearch && <> matching “{appliedSearch}”</>} — click a row to open details, rate con and
                invoicing, or type into the top row to add a load
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(parseInt(e.target.value, 10))}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last year</option>
                <option value={0}>All time</option>
              </select>

              <form onSubmit={handleSearch} className="flex gap-2">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search Pro#, PO#, shipper name or EDI ID…"
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Search
                </button>
                {appliedSearch && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </form>

              <div className="flex items-center gap-2 lg:border-l lg:border-gray-200 lg:pl-3">
                <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Export">
                  <button type="button" onClick={() => handleExport('csv')} disabled={Boolean(exporting)}
                          title="Export the loads shown to CSV"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50">
                    <Download size={14} />
                    {exporting === 'csv' ? 'Exporting…' : 'CSV'}
                  </button>
                  <button type="button" onClick={() => handleExport('xlsx')} disabled={Boolean(exporting)}
                          title="Export the loads shown to Excel"
                          className="px-2.5 py-1.5 text-sm font-medium text-gray-700 bg-white border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50">
                    {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
                  </button>
                </div>
                <button type="button" onClick={() => setBulkOpen(true)}
                        title="Upload shipments from an Excel sheet"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50">
                  <Upload size={14} />
                  Bulk upload
                </button>
                <button type="button" onClick={() => setCreateOpen(true)}
                        title="Full shipment form — stops, references, equipment. For a quick add, type into the top row."
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md shadow-sm hover:bg-primary-700">
                  <Plus size={14} />
                  New shipment
                </button>
              </div>
            </div>
          </div>

          {actionError && (
            <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700 flex justify-between">
              <span>{actionError}</span>
              <button type="button" onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600">✕</button>
            </div>
          )}

          {draftError && (
            <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">
              {draftError}
            </div>
          )}

          {error ? (
            <div className="m-4 bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">{error}</div>
          ) : shipmentsLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            /* Spreadsheet grid: header row, the new-load row under it and the Pro#
               column all stay pinned while the rest scrolls */
            <div ref={scrollRef} className="overflow-auto max-h-[calc(100vh-200px)]">
              <table className="border-collapse text-[11px] tabular-nums">
                <thead>
                  <tr ref={headerRowRef} className="bg-[#d8d8d8]">
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        title={col.title || (col.sort ? `Sort by ${col.label}` : undefined)}
                        onClick={col.sort ? () => handleSort(col.sort) : undefined}
                        className={`${cellBase} ${col.w} sticky top-0 z-40 bg-[#d8d8d8] font-bold text-gray-800 text-center ${
                          col.sticky ? 'left-0 z-50' : ''
                        } ${col.sort ? 'cursor-pointer select-none hover:bg-[#c8c8c8]' : ''}`}
                      >
                        {col.label}{col.sort ? sortIndicator(col.sort) : ''}
                      </th>
                    ))}
                    <th className={`${cellBase} w-28 sticky top-0 z-40 bg-[#d8d8d8] font-bold text-gray-800 text-center`}>
                      Status
                    </th>
                    <th className={`${cellBase} w-24 sticky top-0 z-40 bg-[#d8d8d8] font-bold text-gray-800 text-center`}
                        title="Rate confirmation sent to the carrier">
                      Rate Con
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <NewShipmentRow
                    draft={draft}
                    setDraft={setDraft}
                    onSubmit={submitDraft}
                    onClear={clearDraft}
                    saving={savingDraft}
                    partners={partners}
                    cellBase={cellBase}
                    top={headerHeight}
                    firstInputRef={firstInputRef}
                  />

                  {shipments.map((s) => {
                    const cancelled = s.edi_cancellation_received && !s.cancellation_acknowledged;
                    const rowTint = cancelled
                      ? 'bg-red-50 hover:bg-red-100'
                      : s.has_pending_changes
                        ? 'bg-yellow-50 hover:bg-yellow-100'
                        : 'bg-white hover:bg-primary-50';
                    return (
                      <tr
                        key={s.id}
                        onClick={() => { setDetailsShipment(s); setDetailsOpen(true); }}
                        className={`${rowTint} cursor-pointer transition-colors`}
                      >
                        {COLUMNS.map((col) => {
                          const value = col.render(s);
                          return (
                            <td
                              key={col.key}
                              title={typeof value === 'string' && value ? value : undefined}
                              className={`${cellBase} ${col.w} ${
                                col.align === 'left' ? 'text-left' : col.num ? 'text-right' : 'text-center'
                              } ${col.sticky ? `sticky left-0 z-10 font-medium text-primary-700 ${rowTint}` : 'text-gray-800'}`}
                            >
                              {col.sticky ? (
                                <span className="inline-flex items-center gap-1">
                                  {value}
                                  {cancelled && (
                                    <span title="EDI cancellation received" className="text-red-600 font-bold">✕</span>
                                  )}
                                  {!cancelled && s.has_pending_changes && (
                                    <span title="Pending EDI changes" className="text-yellow-600 font-bold">!</span>
                                  )}
                                </span>
                              ) : col.clamp ? (
                                <div className={`${col.clamp} truncate`}>{value}</div>
                              ) : value}
                            </td>
                          );
                        })}

                        {/* Inline status dropdown — kept out of the Excel layout but needed to work the board */}
                        <td className={`${cellBase} w-28 text-center`} onClick={(e) => e.stopPropagation()}>
                          <select
                            value={s.status}
                            onChange={(e) => handleStatusChange(e, s)}
                            disabled={updatingStatus.has(s.id)}
                            className="w-full text-[11px] border border-gray-300 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
                          >
                            {STATUS_OPTIONS.map((st) => (
                              <option key={st} value={st}>{st.replace('_', ' ')}</option>
                            ))}
                          </select>
                        </td>

                        {/* Rate confirmation — send one, or reopen the one already sent */}
                        <td className={`${cellBase} w-24 text-center`} onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => { setRateConShipment(s); setRateConOpen(true); }}
                            title={rateConTitle(s)}
                            className={`w-full text-[11px] rounded px-1 py-0.5 border transition-colors ${rateConClass(s)}`}
                          >
                            {rateConLabel(s)}
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {shipments.length === 0 && (
                    <tr>
                      <td colSpan={COLUMNS.length + 2} className="text-center py-16 text-gray-500">
                        No loads found for this period — the row above adds one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Crossing this pulls in the next page */}
              <div ref={sentinelRef} className="h-px" />

              {loadingMore && (
                <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  Loading more loads…
                </div>
              )}
              {!hasMore && !loadingMore && shipments.length > 0 && (
                <div className="py-4 text-center text-xs text-gray-400">
                  End of list — {shipments.length.toLocaleString()} of {shipmentsCount.toLocaleString()} loads shown
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ShipmentDetailsModal
        isOpen={detailsOpen}
        onClose={() => { setDetailsOpen(false); setDetailsShipment(null); }}
        shipment={detailsShipment}
        onShipmentUpdated={reload}
        onInvoiceModalClose={reload}
      />

      <CreateShipmentModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onShipmentCreated={reload}
      />

      <BulkUploadShipments
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onUploaded={reload}
      />

      <RateConfirmationModal
        isOpen={rateConOpen}
        onClose={() => { setRateConOpen(false); setRateConShipment(null); reload(); }}
        shipment={rateConShipment}
        onSent={reload}
      />
    </>
  );
}

export default Dashboard;
